import {
  onAuthChanged,
  signInAnonymous,
  signOutUser,
  signInEmailPassword,
  registerWithEmailPassword,
  signInWithGooglePopup,
  sendPasswordResetEmailToUser,
} from "./firebaseClient";

/** Mapa de códigos `auth/...` de Firebase Auth a mensajes en español. */
const AUTH_ERROR_MESSAGES_ES = {
  "auth/email-already-in-use":
    "El email ya está siendo usado. Probá iniciar sesión o usá otro correo.",
  "auth/email-already-exists": "Ese correo ya está registrado.",
  "auth/invalid-email": "El correo electrónico no es válido.",
  "auth/missing-email": "Ingresá un correo electrónico.",
  "auth/user-disabled": "Esta cuenta fue deshabilitada. Contactá al local.",
  "auth/user-not-found": "No hay una cuenta con ese correo.",
  "auth/wrong-password": "Contraseña incorrecta.",
  "auth/invalid-credential":
    "Correo o contraseña incorrectos. Si no recordás la contraseña, usá «Olvidé mi contraseña».",
  "auth/invalid-login-credentials":
    "Correo o contraseña incorrectos. Verificá los datos o recuperá la contraseña.",
  "auth/weak-password": "La contraseña es muy débil. Usá al menos 6 caracteres.",
  "auth/too-many-requests":
    "Demasiados intentos. Esperá unos minutos y probá de nuevo.",
  "auth/network-request-failed": "Sin conexión. Revisá tu internet e intentá otra vez.",
  "auth/popup-closed-by-user": "Cerraste la ventana de Google. Probá de nuevo.",
  "auth/cancelled-popup-request": "Solo podés iniciar una ventana de inicio a la vez.",
  "auth/popup-blocked":
    "El navegador bloqueó la ventana emergente. Permití ventanas para este sitio.",
  "auth/account-exists-with-different-credential":
    "Ya existe una cuenta con ese correo usando otro método (por ejemplo Google). Iniciá sesión con el método que usaste al registrarte.",
  "auth/credential-already-in-use": "Esa credencial ya está vinculada a otra cuenta.",
  "auth/requires-recent-login":
    "Por seguridad tenés que cerrar sesión y volver a entrar para hacer este cambio.",
  "auth/invalid-verification-code": "El código de verificación no es válido o expiró.",
  "auth/invalid-verification-id": "La verificación no es válida. Pedí un nuevo enlace.",
  "auth/expired-action-code": "El enlace expiró. Solicitá uno nuevo.",
  "auth/invalid-action-code": "El enlace no es válido o ya fue usado.",
  "auth/missing-password": "Ingresá tu contraseña.",
  "auth/internal-error": "Error interno del servicio. Intentá de nuevo más tarde.",
};

/**
 * Mensaje legible para errores comunes de Firebase Auth (código `auth/...`).
 * @param {unknown} err
 * @returns {string}
 */
export const formatAuthError = (err) => {
  const code =
    err && typeof err === "object" && "code" in err ? String(err.code) : "";
  const fallback =
    err && typeof err === "object" && "message" in err
      ? String(err.message)
      : "";

  if (code === "auth/admin-restricted-operation") {
    return (
      "Firebase bloqueó la operación. En la consola: Authentication → Sign-in method, activá " +
      "«Correo/contraseña» y «Anónimo». En Google Cloud → Credenciales, si la API key tiene " +
      "restricciones, permití la API «Identity Toolkit API»."
    );
  }
  if (code === "auth/operation-not-allowed") {
    return "Este método de inicio de sesión no está habilitado en Firebase (Sign-in method).";
  }
  if (code === "auth/invalid-api-key") {
    return "API key de Firebase inválida o no permitida para este dominio.";
  }

  if (code && AUTH_ERROR_MESSAGES_ES[code]) {
    return AUTH_ERROR_MESSAGES_ES[code];
  }

  if (
    fallback &&
    (fallback.includes("Firebase no disponible") ||
      fallback.includes("Firebase no está"))
  ) {
    return "No pudimos conectar con el servidor. Revisá la configuración o intentá más tarde.";
  }

  if (fallback && /^Firebase: Error \(auth\//.test(fallback)) {
    return "No se pudo completar la operación. Probá de nuevo.";
  }

  return "";
};

/**
 * Devuelve un mensaje seguro para mostrar al usuario (nunca el texto crudo en inglés de Firebase).
 * @param {unknown} err
 * @returns {string}
 */
export const formatAuthErrorSafe = (err) => {
  const msg = formatAuthError(err);
  if (msg) return msg;
  return "No se pudo completar la operación. Probá de nuevo.";
};

/**
 * Inicializa sesión pública: garantiza usuario Firebase (anónimo si hace falta).
 * @param {(state: { user: import('firebase/auth').User | null; ready: boolean; error: string | null; isAnonymous?: boolean }) => void} onChange
 * @returns {() => void} unsubscribe
 */
export const initPublicAuth = (onChange) => {
  onChange({ user: null, ready: false, error: null });

  const unsub = onAuthChanged(async (user) => {
    if (!user) {
      try {
        await signInAnonymous();
      } catch (err) {
        onChange({
          user: null,
          ready: true,
          error: formatAuthErrorSafe(err),
          isAnonymous: false,
        });
      }
      return;
    }
    onChange({
      user,
      ready: true,
      error: null,
      isAnonymous: user.isAnonymous,
    });
  });

  return unsub;
};

export const loginWithEmail = (email, password) =>
  signInEmailPassword(email, password);

export const registerWithEmail = (email, password, displayName) =>
  registerWithEmailPassword(email, password, displayName);

export const loginWithGoogle = () => signInWithGooglePopup();

/** Envía el correo de restablecimiento de contraseña (Firebase Auth). */
export const requestPasswordReset = (email) =>
  sendPasswordResetEmailToUser(email);

/** Cierra sesión; el listener volverá a crear sesión anónima. */
export const logoutPublic = () => signOutUser();
